import { useState } from 'react';
import { galleryItems } from '../../../mocks/galleryData';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

export default function GallerySection() {
  const [activeSliders, setActiveSliders] = useState<Record<string, number>>({});
  const { ref, isVisible } = useScrollAnimation();

  const getSliderPos = (id: string) => activeSliders[id] ?? 50;

  const handleDrag = (id: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setActiveSliders((prev) => ({ ...prev, [id]: Math.min(95, Math.max(5, x)) }));
  };

  return (
    <section className="py-24 bg-brand-dark" id="gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
            <div className="h-px w-8 bg-brand-amber" />
            Before &amp; After Gallery
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            See the BBM Services Difference
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Drag the slider to reveal the transformation. Real results from real Jacksonville homes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item) => (
            <div key={item.id} className="rounded-xl overflow-hidden border border-white/10">
              {/* Slider */}
              <div
                className="relative h-64 cursor-col-resize select-none overflow-hidden"
                onMouseMove={(e) => handleDrag(item.id, e)}
              >
                {/* After image (full) */}
                <img
                  src={item.afterImage}
                  alt={`After ${item.label}`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                {/* Before image (clipped) */}
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${getSliderPos(item.id)}%` }}
                >
                  <img
                    src={item.beforeImage}
                    alt={`Before ${item.label}`}
                    className="w-full h-full object-cover object-top"
                    style={{ width: `${10000 / getSliderPos(item.id)}%`, maxWidth: 'none' }}
                  />
                </div>
                {/* Divider line */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white z-10"
                  style={{ left: `${getSliderPos(item.id)}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-arrow-left-right-line text-brand-dark text-xs" />
                  </div>
                </div>
                {/* Labels */}
                <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-md z-10">Before</div>
                <div className="absolute top-3 right-3 bg-brand-amber text-white text-xs font-semibold px-2.5 py-1 rounded-md z-10">After</div>
              </div>
              {/* Caption */}
              <div className="bg-brand-light/60 border-t border-white/10 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{item.label}</span>
                  <span className="text-white/50 text-xs">{item.caption.split('—')[0].trim()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-brand-amber text-white hover:text-brand-amber font-semibold py-3 px-8 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            View Full Gallery
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
